import {Component, OnInit} from '@angular/core';
import {ImageUploadComponent, PreparedBatch} from "../../../../core/components/image-upload/image-upload.component";
import {TranslocoPipe, TranslocoService} from "@jsverse/transloco";
import {PortfolioService} from '../../../services/portfolio/portfolio.service';
import {PortfolioPhoto} from '../../../../core/entities/portfolio/portfolio-photo.entity';
import {ToastrService} from 'ngx-toastr';
import {CdkDragDrop, DragDropModule, moveItemInArray} from '@angular/cdk/drag-drop';
import {debounceTime, Subject} from 'rxjs';
import {AppImageComponent} from '../../../../core/components/image/image';

@Component({
  selector: 'app-public-profile-portfolio',
  imports: [
    ImageUploadComponent,
    TranslocoPipe,
    DragDropModule,
    AppImageComponent,
  ],
  templateUrl: './public-profile-portfolio.html',
  standalone: true,
  styleUrl: './public-profile-portfolio.scss'
})
export class PublicProfilePortfolio implements OnInit {
  public portfolioPhotos: PortfolioPhoto[]|null = null;

  private orderChanged$ = new Subject<void>();

  public constructor(
    private readonly portfolioService: PortfolioService,
    private readonly toastr: ToastrService,
    private readonly transloco: TranslocoService,
  ) {
  }

  public ngOnInit() {
    this.portfolioService.getPortfolio().subscribe(
      (portfolioPhotos) => {
        this.portfolioPhotos = portfolioPhotos;
      }
    );

    this.orderChanged$
      .pipe(debounceTime(1000))
      .subscribe(() => this.saveOrder());
  }

  public onPortfolioBatchReady(batch: PreparedBatch): void {
    if (!batch.files.length) {
      return;
    }

    this.portfolioService.addPortfolioPhotos(batch.files).subscribe({
      next: (portfolioPhotos) => {
        this.portfolioPhotos = [
          ...(this.portfolioPhotos ?? []),
          ...portfolioPhotos,
        ];

        this.toastr.success(
          this.transloco.translate('public-profile.portfolio.add-photo.success.content'),
          this.transloco.translate('public-profile.portfolio.add-photo.success.title'),
        );
      },
      error: () => {
        this.toastr.error(
          this.transloco.translate('public-profile.portfolio.add-photo.error.content'),
          this.transloco.translate('public-profile.portfolio.add-photo.error.title'),
        );
      },
    });
  }

  public deletePortfolioPhoto(portfolioPhoto: PortfolioPhoto): void {
    this.portfolioService.deletePhoto(portfolioPhoto.id).subscribe(
      () => {
        this.portfolioPhotos = this.portfolioPhotos?.filter(p => p.id !== portfolioPhoto.id) ?? [];
        this.toastr.success(
          this.transloco.translate('public-profile.portfolio.delete-photo.success.content'),
          this.transloco.translate('public-profile.portfolio.delete-photo.success.title'),
        );
      }, () => {
        this.toastr.error(
          this.transloco.translate('public-profile.portfolio.delete-photo.error.content'),
          this.transloco.translate('public-profile.portfolio.delete-photo.error.title'),
        );
      }
    )
  }

  public drop(event: CdkDragDrop<PortfolioPhoto[]>): void {
    if (!this.portfolioPhotos) {
      return;
    }
    moveItemInArray(this.portfolioPhotos, event.previousIndex, event.currentIndex);
    this.orderChanged$.next();
  }

  public saveOrder(): void {
    if (!this.portfolioPhotos) {
      return;
    }
    const payload = this.portfolioPhotos.map((s, index) => ({
      id: s.id,
      position: index
    }));

    this.portfolioService.updateOrder(payload).subscribe({
      next: () => this.toastr.success(
        this.transloco.translate('public-profile.portfolio.order.success.content'),
        this.transloco.translate('public-profile.portfolio.order.success.title')
      ),
      error: () => this.toastr.error(
        this.transloco.translate('public-profile.portfolio.order.error.content'),
        this.transloco.translate('public-profile.portfolio.order.error.title')
      )
    });
  }

}
